/**
 * ArchLang Aspects (aspect values as references + grouping identities)
 *
 * An aspect value carries one of two cross-cutting meanings, decided by
 * its lexical form:
 *
 *   - string  (`aspects.netzone: "dmz"`)  → an **aspect**: a pure
 *     aspect-space identity keyed by `(keyPath, value)`. Modules that
 *     share an aspect form a directed `member → group` web. The group
 *     is abstract (a synthesized node).
 *
 *   - identifier (`aspects.netzone: dmz`) → a **reference** to a module
 *     named `dmz`. Strict: must resolve to a real module or it is a
 *     diagnostic. The group is concrete (an existing module node).
 *
 * Numbers / booleans carry neither meaning. Lists are flattened: each
 * item is classified on its own, so `aspects.tag: ["a", "b"]` is
 * multi-membership across two aspects.
 *
 * This module is pure — it only walks a `ResolvedModel` and classifies.
 * Diagnostics (validation.ts), references/rename (lsp), and the overlay
 * graph (board) are all built on top of `forEachAspectValue`.
 */

import type { QualifiedName, SourceSpan, Value } from "./types.js";
import type {
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "./resolver.js";

/** Which cross-cutting meaning a single (atomic) aspect value carries. */
export type AspectValueClass = "aspect" | "ref" | "other";

/**
 * Classify one atomic aspect value. Lists are NOT handled here — callers
 * flatten lists and classify each item (see {@link forEachAspectValue}).
 */
export function classifyAspectValue(v: Value | undefined): AspectValueClass {
  if (!v) return "other";
  switch (v.kind) {
    case "string":
      return "aspect";
    case "identifier":
    case "ref":
      return "ref";
    default:
      return "other";
  }
}

/** What kind of body an aspect was declared on. `process` carriers (v0.10 §5.1)
 *  are non-module and never cascade INTO structural children — presence is
 *  suppressed for them (spec §4.4). */
export type AspectOwnerType = "module" | "surface" | "interface" | "process";

/** One atomic aspect-value occurrence, located in the resolved model. */
export interface AspectValueOccurrence {
  /** The aspect key, e.g. `["security", "zone"]` or `["netzone"]`. */
  readonly keyPath: QualifiedName;
  /** The atomic value (string / identifier / number / boolean — never a list). */
  readonly value: Value;
  /** Cross-cutting meaning of this value. */
  readonly cls: AspectValueClass;
  readonly ownerType: AspectOwnerType;
  /** Qualified name of the owning module / surface / interface. */
  readonly ownerPath: QualifiedName;
  /** Best span for the value itself, falling back to the aspect entry. */
  readonly source?: SourceSpan;
}

/**
 * Stable string key for an aspect identity. Two occurrences with the
 * same `(keyPath, value)` denote the same aspect.
 */
export function aspectKey(keyPath: QualifiedName, value: string): string {
  return `${keyPath.join(".")}=${JSON.stringify(value)}`;
}

/**
 * Walk every aspect-value occurrence in the model, flattening lists into
 * atomic items. Invokes `cb` once per atomic value, in document order
 * within each body. Owner paths are qualified dotted names.
 */
export function forEachAspectValue(
  model: ResolvedModel,
  cb: (occ: AspectValueOccurrence) => void
): void {
  const visitBodyAspects = (
    aspects: readonly { path: QualifiedName; value?: Value; source?: SourceSpan }[],
    ownerType: AspectOwnerType,
    ownerPath: QualifiedName
  ): void => {
    for (const aspect of aspects) {
      if (!aspect.value) continue;
      emitValue(aspect.value, aspect.path, ownerType, ownerPath, aspect.source, cb);
    }
  };

  const visitInterface = (i: ResolvedInterface, parentPath: QualifiedName): void => {
    visitBodyAspects(i.body.aspects, "interface", [...parentPath, i.name]);
  };

  const visitSurface = (f: ResolvedSurface, parentPath: QualifiedName): void => {
    const myPath = [...parentPath, f.name];
    visitBodyAspects(f.body.aspects, "surface", myPath);
    for (const i of f.interfaces) visitInterface(i, myPath);
    for (const sub of f.surfaces) visitSurface(sub, myPath);
  };

  const visitModule = (m: ResolvedModule, parentPath: QualifiedName): void => {
    const myPath = [...parentPath, m.name];
    visitBodyAspects(m.body.aspects, "module", myPath);
    for (const i of m.interfaces) visitInterface(i, myPath);
    for (const f of m.surfaces) visitSurface(f, myPath);
    for (const child of m.modules) visitModule(child, myPath);
  };

  for (const m of model.modules) visitModule(m, []);

  // v0.10 §5.1: processes are aspect carriers too (resolved via owner cascade in
  // applyCascade). Their qualified path is `owner.name`; anonymous processes
  // carry no name and declare no aspects, so they contribute nothing.
  for (const pm of model.processModels ?? []) {
    if (!pm.aspects || pm.aspects.length === 0 || pm.name === "") continue;
    visitBodyAspects(pm.aspects, "process", [...(pm.ownerPath ?? []), pm.name]);
  }
}

// =============================================================================
// Aspect graph — the directed `member → group` overlay web
// =============================================================================

/**
 * A group node in the overlay. Either an **aspect** (synthesized from a
 * string aspect value — no backing module) or a **module** (the target of
 * one or more bare references — a real, materialized node).
 */
export interface AspectGroup {
  /** `aspect:<key>=<value>` or `module:<qualified-path>`. */
  readonly id: string;
  readonly kind: "aspect" | "module";
  /** Aspect key for aspect groups; undefined for module groups. */
  readonly keyPath?: QualifiedName;
  /** Aspect value, or the module's simple name. */
  readonly label: string;
  /** Qualified path for a module group; undefined for aspect groups. */
  readonly modulePath?: QualifiedName;
  /** Distinct members pointing at this group, in first-seen order. */
  readonly members: readonly QualifiedName[];
}

/** A directed overlay edge: a member instance points at a group. */
export interface AspectEdge {
  readonly from: QualifiedName;
  readonly toGroupId: string;
}

export interface AspectGraph {
  readonly groups: readonly AspectGroup[];
  readonly edges: readonly AspectEdge[];
}

/**
 * Derive the overlay web from a resolved model. Every aspect value emits a
 * directed `member → group` edge:
 *   - string value → an aspect group keyed `(keyPath, value)` (synthesized).
 *   - bare value   → the module it names (the group is that real module).
 *
 * Bare values that don't resolve to a module are skipped (validation
 * reports them as `ASPECT_BAD_REF`). The graph is pure data — rendering,
 * gating, and styling live in the consumer.
 */
export function buildAspectGraph(model: ResolvedModel): AspectGraph {
  const modulePaths = buildModulePathIndex(model);
  const groups = new Map<string, { group: AspectGroup; members: QualifiedName[]; seen: Set<string> }>();
  const edges: AspectEdge[] = [];

  const ensure = (
    id: string,
    make: () => AspectGroup
  ): { group: AspectGroup; members: QualifiedName[]; seen: Set<string> } => {
    let g = groups.get(id);
    if (!g) {
      g = { group: make(), members: [], seen: new Set() };
      groups.set(id, g);
    }
    return g;
  };

  forEachAspectValue(model, (occ) => {
    let id: string;
    let make: () => AspectGroup;
    if (occ.cls === "aspect" && occ.value.kind === "string") {
      const value = occ.value.text;
      id = `aspect:${aspectKey(occ.keyPath, value)}`;
      make = () => ({ id, kind: "aspect", keyPath: occ.keyPath, label: value, members: [] });
    } else if (occ.cls === "ref" && occ.value.kind === "identifier") {
      const target = modulePaths.get(occ.value.text);
      if (!target) return; // unresolved bare ref — ASPECT_BAD_REF handles it
      id = `module:${target.join(".")}`;
      make = () => ({ id, kind: "module", label: occ.value.kind === "identifier" ? occ.value.text : "", modulePath: target, members: [] });
    } else {
      return; // number / boolean — not part of the web
    }
    const g = ensure(id, make);
    const memberKey = occ.ownerPath.join(".");
    if (!g.seen.has(memberKey)) {
      g.seen.add(memberKey);
      g.members.push(occ.ownerPath);
    }
    edges.push({ from: occ.ownerPath, toGroupId: id });
  });

  return {
    groups: [...groups.values()].map((g) => ({ ...g.group, members: g.members })),
    edges,
  };
}

/** Map every module's simple name and qualified name to its qualified path. */
function buildModulePathIndex(model: ResolvedModel): Map<string, QualifiedName> {
  const index = new Map<string, QualifiedName>();
  const visit = (m: ResolvedModule, parent: QualifiedName): void => {
    const path = [...parent, m.name];
    const qualified = path.join(".");
    // Qualified name always wins; a simple name only fills an empty slot so
    // the first declaration of a given short name is the default target.
    index.set(qualified, path);
    if (!index.has(m.name)) index.set(m.name, path);
    for (const child of m.modules) visit(child, path);
  };
  for (const m of model.modules) visit(m, []);
  return index;
}

/** Emit a value, flattening lists into their atomic items. */
function emitValue(
  value: Value,
  keyPath: QualifiedName,
  ownerType: AspectOwnerType,
  ownerPath: QualifiedName,
  aspectSource: SourceSpan | undefined,
  cb: (occ: AspectValueOccurrence) => void
): void {
  if (value.kind === "list") {
    for (const item of value.items) {
      emitValue(item, keyPath, ownerType, ownerPath, aspectSource, cb);
    }
    return;
  }
  cb({
    keyPath,
    value,
    cls: classifyAspectValue(value),
    ownerType,
    ownerPath,
    source: value.source ?? aspectSource,
  });
}
